import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useOutletContext } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AppTheme from '../../styles/AppTheme';
import { useUser } from '../../contexts/UserContext';
import RazorpayReconcileDialog from '../../components/accounts/receipts/RazorpayReconcileDialog';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.fmb52.com/api';

const SECTOR_OPTIONS = ['ALL', 'BURHANI', 'EZZY', 'MOHAMMEDI', 'SHUJAI', 'ZAINY'];

const formatInr = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DueList() {
  const { token } = useUser();
  const outletContext = useOutletContext() || {};
  const selectedYear =
    outletContext.selectedYear != null && String(outletContext.selectedYear) !== ''
      ? String(outletContext.selectedYear)
      : '';
  const selectedSector = outletContext.selectedSector || ['all'];
  const selectedSubSector = outletContext.selectedSubSector || ['all'];

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('due');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ families: 0, commitment: 0, paid: 0, due: 0 });
  const [skipCount, setSkipCount] = useState(0);
  const [skipFilter, setSkipFilter] = useState('all');
  const [skipBusy, setSkipBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [customText, setCustomText] = useState('Please clear your pending hub dues at the earliest.');
  const [waMode, setWaMode] = useState('simulate');
  const [overrideMobile, setOverrideMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const skippedSelected = useMemo(() => {
    const skipped = new Set(rows.filter((r) => r.skipped).map((r) => String(r.family_id)));
    return selected.filter((id) => skipped.has(id));
  }, [rows, selected]);
  const manualSkippedSelected = useMemo(() => {
    const manual = new Set(
      rows.filter((r) => r.skip_manual).map((r) => String(r.family_id))
    );
    return selected.filter((id) => manual.has(id));
  }, [rows, selected]);
  const sendableSelected = useMemo(
    () => selected.filter((id) => !skippedSelected.includes(id)),
    [selected, skippedSelected]
  );
  const sendableIndividual = sendableSelected.length === 1;
  const sendableRow = useMemo(
    () =>
      sendableIndividual
        ? rows.find((r) => String(r.family_id) === String(sendableSelected[0]))
        : null,
    [sendableIndividual, rows, sendableSelected]
  );

  const openSendDialog = () => {
    if (sendableSelected.length === 1) {
      const row = rows.find((r) => String(r.family_id) === String(sendableSelected[0]));
      setOverrideMobile(row?.mobile ? String(row.mobile) : '');
    } else {
      setOverrideMobile('');
    }
    setSendOpen(true);
  };

  const updateSkipList = async (action, familyIds) => {
    if (!token || !selectedYear || !familyIds.length) return;
    setSkipBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/due-list/${action === 'skip' ? 'skip' : 'unskip'}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          year: selectedYear,
          family_ids: familyIds,
        }),
      });
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.message || `Failed to ${action} families`);
      }
      setSnack({
        open: true,
        message: json.message || (action === 'skip' ? 'Added to skip list.' : 'Removed from skip list.'),
        severity: 'success',
      });
      await fetchDueList();
    } catch (e) {
      setSnack({ open: true, message: e.message || `Failed to ${action}`, severity: 'error' });
    } finally {
      setSkipBusy(false);
    }
  };

  useEffect(() => {
    document.title = 'Due List - FMB 52';
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [selectedYear, selectedSector, selectedSubSector, debouncedSearch, sectorFilter, sortBy, sortDir, skipFilter]);

  const fetchDueList = useCallback(async () => {
    if (!token || !selectedYear) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/due-list`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          year: selectedYear,
          sector: selectedSector?.length ? selectedSector : ['all'],
          sub_sector: selectedSubSector?.length ? selectedSubSector : ['all'],
          sector_name: sectorFilter !== 'ALL' ? sectorFilter : undefined,
          skip_filter: skipFilter,
          search: debouncedSearch || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
          page: page + 1,
          per_page: perPage,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || 'Failed to load due list');
      setRows(Array.isArray(json?.data) ? json.data : []);
      const pag = json?.pagination || {};
      setTotal(Number(pag.total || 0));
      if (json?.totals) {
        setTotals({
          families: Number(json.totals.families || pag.total || 0),
          commitment: Number(json.totals.commitment || 0),
          paid: Number(json.totals.paid || 0),
          due: Number(json.totals.due || 0),
        });
      } else {
        setTotals({ families: Number(pag.total || 0), commitment: 0, paid: 0, due: 0 });
      }
      setSkipCount(Number(json?.skip_count || 0));
      setSelected([]);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTotals({ families: 0, commitment: 0, paid: 0, due: 0 });
      setSkipCount(0);
      setError(e.message || 'Failed to load due list');
    } finally {
      setLoading(false);
    }
  }, [
    token,
    selectedYear,
    selectedSector,
    selectedSubSector,
    sectorFilter,
    debouncedSearch,
    skipFilter,
    sortBy,
    sortDir,
    page,
    perPage,
  ]);

  useEffect(() => {
    fetchDueList();
  }, [fetchDueList]);

  const pageIds = useMemo(() => rows.map((r) => String(r.family_id)), [rows]);
  const allSelected =
    pageIds.length > 0 && selected.length === pageIds.length && pageIds.every((id) => selected.includes(id));

  const toggleAll = () => {
    setSelected(allSelected ? [] : pageIds);
  };

  const toggleOne = (familyId) => {
    const id = String(familyId);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'due' || field === 'commitment' || field === 'paid' ? 'desc' : 'asc');
    }
  };

  const SortHead = ({ field, label, align = 'left' }) => (
    <TableCell align={align} sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const handleSend = async () => {
    if (!token || !selectedYear) return;
    if (!customText.trim()) {
      setSnack({ open: true, message: 'Custom text is required.', severity: 'warning' });
      return;
    }
    if (sendableSelected.length === 0) {
      setSnack({
        open: true,
        message: selected.length > 0
          ? 'Selected families are skipped (skip list or receipt in the last 10 days).'
          : 'Select at least one family.',
        severity: 'warning',
      });
      return;
    }

    if (sendableIndividual && !overrideMobile.trim()) {
      setSnack({ open: true, message: 'Enter a phone number to send to.', severity: 'warning' });
      return;
    }

    setSending(true);
    try {
      const payload = {
        mode: waMode,
        year: selectedYear,
        custom_text: customText.trim(),
        family_ids: sendableSelected,
        sector: selectedSector?.length ? selectedSector : ['all'],
        sub_sector: selectedSubSector?.length ? selectedSubSector : ['all'],
      };
      if (sendableIndividual) {
        payload.mobile = overrideMobile.trim();
      }

      const resp = await fetch(`${API_BASE}/wa/faiz_due_rzp`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.message || 'Failed to send WhatsApp');
      }

      const skippedNote =
        json.skipped_excluded > 0
          ? ` (${json.skipped_excluded} skipped: skip list or receipt in last 10 days)`
          : '';
      const msg =
        waMode === 'simulate'
          ? `Simulate OK — ${json.count ?? sendableSelected.length} eligible (preview ${json.preview_count ?? 0})${skippedNote}`
          : `${json.message || 'WhatsApp queued successfully'}${skippedNote}`;

      setSnack({ open: true, message: msg, severity: 'success' });
      setSendOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Send failed', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline />
      <Box sx={{ width: '100%', mt: 10, px: 2, pb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Due List
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Year: ${selectedYear || '—'}`} color="primary" variant="outlined" />
                <Chip size="small" label={`Families: ${totals.families}`} />
                {sectorFilter !== 'ALL' && (
                  <Chip size="small" color="info" variant="outlined" label={`Sector: ${sectorFilter}`} />
                )}
                <Chip size="small" color="error" variant="outlined" label={`Total Due: ₹${formatInr(totals.due)}`} />
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<BlockIcon />}
                  label={`Skip list: ${skipCount}`}
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sector</InputLabel>
                <Select
                  label="Sector"
                  value={sectorFilter}
                  onChange={(e) => {
                    setSectorFilter(e.target.value);
                    setSelected([]);
                  }}
                >
                  {SECTOR_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s === 'ALL' ? 'All Sectors' : s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={`${sortBy}:${sortDir}`}
                  onChange={(e) => {
                    const [field, dir] = String(e.target.value).split(':');
                    setSortBy(field);
                    setSortDir(dir === 'asc' ? 'asc' : 'desc');
                  }}
                >
                  <MenuItem value="due:desc">Due (High → Low)</MenuItem>
                  <MenuItem value="due:asc">Due (Low → High)</MenuItem>
                  <MenuItem value="commitment:desc">Commitment (High → Low)</MenuItem>
                  <MenuItem value="commitment:asc">Commitment (Low → High)</MenuItem>
                  <MenuItem value="paid:desc">Paid (High → Low)</MenuItem>
                  <MenuItem value="paid:asc">Paid (Low → High)</MenuItem>
                  <MenuItem value="name:asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name:desc">Name (Z → A)</MenuItem>
                  <MenuItem value="its:asc">ITS (A → Z)</MenuItem>
                  <MenuItem value="sector:asc">Sector (A → Z)</MenuItem>
                  <MenuItem value="sub_sector:asc">Sub-Sector (A → Z)</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Skip list</InputLabel>
                <Select
                  label="Skip list"
                  value={skipFilter}
                  onChange={(e) => {
                    setSkipFilter(e.target.value);
                    setSelected([]);
                  }}
                >
                  <MenuItem value="all">All families</MenuItem>
                  <MenuItem value="sendable">Sendable only</MenuItem>
                  <MenuItem value="skipped">Skip list only</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search name / ITS / mobile..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelected([]);
                }}
                sx={{ minWidth: 220 }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDueList} disabled={loading}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                onClick={() => setReconcileOpen(true)}
              >
                Reconcile Razorpay
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<BlockIcon />}
                disabled={loading || skipBusy || selected.length === 0}
                onClick={() => updateSkipList('skip', selected)}
              >
                Skip ({selected.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                disabled={loading || skipBusy || manualSkippedSelected.length === 0}
                onClick={() => updateSkipList('unskip', manualSkippedSelected)}
              >
                Unskip ({manualSkippedSelected.length})
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                disabled={loading || sendableSelected.length === 0}
                onClick={openSendDialog}
              >
                Send WA ({sendableSelected.length})
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 620 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selected.length > 0 && !allSelected}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <SortHead field="its" label="ITS" />
                      <SortHead field="name" label="Name" />
                      <SortHead field="mobile" label="Mobile" />
                      <SortHead field="sector" label="Sector" />
                      <SortHead field="sub_sector" label="Sub-Sector" />
                      <SortHead field="commitment" label="Commitment" align="right" />
                      <SortHead field="paid" label="Paid" align="right" />
                      <SortHead field="due" label="Due" align="right" />
                      <TableCell align="center">Skip</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No dues found for this year / filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((r) => {
                        const id = String(r.family_id);
                        const skipped = Boolean(r.skipped);
                        const recentReceipt = r.skip_reason === 'recent_receipt';
                        const skipLabel = recentReceipt ? 'Receipt 10d' : 'Skipped';
                        return (
                          <TableRow
                            key={id}
                            hover
                            selected={selected.includes(id)}
                            sx={skipped ? { bgcolor: 'action.hover', opacity: 0.78 } : undefined}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={selected.includes(id)} onChange={() => toggleOne(id)} />
                            </TableCell>
                            <TableCell>{r.its}</TableCell>
                            <TableCell>
                              {r.name}
                              {skipped && (
                                <Chip size="small" color="warning" label={skipLabel} sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{r.mobile}</TableCell>
                            <TableCell>{r.sector}</TableCell>
                            <TableCell>{r.sub_sector}</TableCell>
                            <TableCell align="right">{formatInr(r.commitment)}</TableCell>
                            <TableCell align="right">{formatInr(r.paid)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                              {formatInr(r.due)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  recentReceipt && !r.skip_manual
                                    ? 'Skipped: receipt created in the last 10 days'
                                    : skipped
                                      ? 'Remove from skip list'
                                      : 'Add to skip list'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color={skipped ? 'warning' : 'default'}
                                    disabled={skipBusy || (recentReceipt && !r.skip_manual)}
                                    onClick={() =>
                                      updateSkipList(r.skip_manual ? 'unskip' : 'skip', [id])
                                    }
                                  >
                                    {r.skip_manual ? (
                                      <UndoIcon fontSize="small" />
                                    ) : (
                                      <BlockIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100, 200, 500]}
              />
            </>
          )}
        </Paper>

        <Dialog open={sendOpen} onClose={() => !sending && setSendOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Send Due WhatsApp (faiz_due_rzp)</DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              Params: Name, ITS, Commitment, Paid, Due, Custom Text + Pay Now link.
              Skipped families and families with a receipt in the last 10 days are never sent.
            </Alert>
            {skippedSelected.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {skippedSelected.length} selected {skippedSelected.length === 1 ? 'family is' : 'families are'} on
                the skip list and will be excluded.
              </Alert>
            )}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Mode</InputLabel>
              <Select label="Mode" value={waMode} onChange={(e) => setWaMode(e.target.value)}>
                <MenuItem value="simulate">Simulate (preview only)</MenuItem>
                <MenuItem value="demo">Demo (1 message to test number)</MenuItem>
                <MenuItem value="live">Live (queue selected)</MenuItem>
              </Select>
            </FormControl>
            {sendableIndividual && (
              <TextField
                label="Phone number"
                value={overrideMobile}
                onChange={(e) => setOverrideMobile(e.target.value)}
                fullWidth
                size="small"
                margin="normal"
                helperText={
                  sendableRow?.name
                    ? `Sending for ${sendableRow.name} (${sendableRow.its || sendableRow.family_id})`
                    : 'Number to receive this WhatsApp'
                }
                placeholder="9198XXXXXXXX"
                InputLabelProps={{ shrink: true }}
                required
              />
            )}
            <TextField
              label="Custom Text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Will send to {sendableSelected.length} of {selected.length} selected
              {skippedSelected.length > 0 ? ` (${skippedSelected.length} skipped)` : ''}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
            >
              {waMode === 'simulate' ? 'Preview' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

        <RazorpayReconcileDialog
          open={reconcileOpen}
          onClose={() => setReconcileOpen(false)}
          token={token}
          onCreated={(json) => {
            fetchDueList();
            if (json?.message) {
              setSnack({
                open: true,
                message: json.message,
                severity: json.errors?.length ? 'warning' : 'success',
              });
            }
          }}
        />

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </AppTheme>
  );
}
